import { useEffect } from 'react';
import {
  captureException,
  browserTracingIntegration,
  tanstackRouterBrowserTracingIntegration,
  withScope,
} from '@sentry/react';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { authnClient } from '@frontend/auth';
import { getUser } from '@frontend/auth-helpers';
import envs from '@frontend/env';
import { TAB_ID, registerOpenTab } from '@frontend/tab-management';
import {
  getNavigationStartTime,
  sentry,
  usePageLoadSpanMarker,
  tagThirdPartySpan,
  getSentryConfig,
} from '@frontend/tracking';
import { ThemeProvider, AlertsProvider, SpinningLoader } from '@frontend/design-system';
import '@design-system/tokens';
import { ErrorBoundary, GatekeeperError } from './components/ErrorBoundary';
import { Layout } from './components/layout';
import { routeTree } from './routeTree.gen';
import { beforeSend, sentryEnvironment } from './services/sentry-utils';

const router = createRouter({
  routeTree,
  defaultPendingComponent: () => (
    <Layout background='blue'>
      <SpinningLoader key='loader' size='medium' />
    </Layout>
  ),
  defaultErrorComponent: ({ error }) => <GatekeeperError error={error} />,
  defaultOnCatch: (error) => {
    withScope((scope) => {
      const userId = getUser()?.userID;
      if (userId) {
        scope.setUser({ id: userId });
      }
      scope.setTag('boundary.tier', 'route');
      scope.setTag('boundary.component', 'gatekeeper-router');
      scope.setLevel('error');
      captureException(error);
    });
    /**
     * In test environments, we want to throw the error so that tests fail, we get early feedback, and proper stack traces.
     * In non-test environments, we don't want to throw the error because it will be caught by the ErrorBoundary
     * and logged to Sentry.
     */
    if (import.meta.env.MODE === 'test') throw error;
  },
  context: { auth: authnClient },
});

sentry.init(
  getSentryConfig({
    dsn: envs.SENTRY_DSN,
    release: envs.VERSION,
    environment: sentryEnvironment,
    // "HTTP error 401" - see new-auth/fetch for where this error message comes from
    // "TypeError: Failed to fetch" -- the browser throws this when fetch fails to even
    //    send a request for example, if the user is offline or the URL is invalid. Due
    //    to how common internet connectivity issues are, we ignore this error to avoid
    //    overwhelming Sentry with noise.
    // 410 Gone - occurs when hydra sees a user try to use a login request that has been already handled
    // 429 Account currently locked - the user has too many failed login attempts
    // "login request was cancelled" - occurs when the user logging in differs from the user in the active
    //    Hydra session. This is an expected condition handled gracefully in the UI (redirects to re-initiate
    //    the login flow). It can surface as an unhandled promise rejection via async event handlers.
    ignoreErrors: [
      /(HTTP error|Request failed with status) (401|403|410|429)/i,
      'TypeError: Failed to fetch',
      /410 Gone/i,
      'login request was cancelled',
    ],
    denyUrls: ['chrome-extension://'],
    integrations: [
      browserTracingIntegration({ instrumentNavigation: true }),
      tanstackRouterBrowserTracingIntegration(router) as any, // ignoring type error for now
    ],
    tracesSampleRate: sentryEnvironment === 'production' ? 0.1 : 0,
    // gatekeeper does not apply the pendo-static span filter
    beforeSendSpan: (span: any) => tagThirdPartySpan(span),
    // beforeSend is where we apply our sampling logic, so we do not set sampleRate in the config to avoid double-sampling.
    beforeSend: beforeSend,
  })
);

export default function Root() {
  if (import.meta.env.MODE !== 'test') {
    console.log('version: ', envs.VERSION);
  }
  /* on first load do these checks:
      handle routing, is it a call back, magic link, password reset, etc.
      check if user is already signed in, if so, redirect to the requested app
      initialize state key so we can have multiple tabs open and logging into different apps
  */

  useEffect(() => registerOpenTab(TAB_ID), []);

  // Set the startTime to when the browser starts handling a request which gives us the complete time till the user can do something in gatekeeper
  usePageLoadSpanMarker('Gatekeeper app mounted', 'ui.gatekeeper.mounted', getNavigationStartTime());

  return (
    <ErrorBoundary tier='app' feature='gatekeeper.root'>
      <ThemeProvider includeEmotionTheme skipAnimation={!!window?.Cypress || import.meta.env.MODE === 'test'}>
        <AlertsProvider>
          <RouterProvider
            router={router}
            context={{
              auth: authnClient,
            }}
          />
        </AlertsProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

declare global {
  interface Window {
    Cypress?: any;
  }
}
